import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { exceptionsEnabledCategories, IssueScopeKind } from '../../../../shared/issuesV2';
import { isFreeTier } from '../../../../shared/subscriptions';
import { window } from '../../../globals';
import * as IssuesV2Store from '../../../redux/issuesV2';
import { ignoreOrResolveWordPast } from '../../../utils';
import Flex from '../../generic/Flex';
import PaidFeatureGate from '../../generic/Gates/PaidFeatureGate';
import NotFound from '../../generic/NotFound';
import Text from '../../generic/Text';
import BackToIssues from '../IssueDetails/BackToIssues';
import type { IssuesContainerProps } from '../IssuesContainer';

import AddIgnoreRuleButton from './AddIgnoreRuleButton';
import ExportIgnoreRulesButton from './ExportIgnoreRulesButton';
import IssueExceptionsHeader from './IssueExceptionsHeader';
import IssueExceptionsTable from './IssueExceptionsTable';
import useIssueExceptions from './useIssueExceptions';
import type { UseIssueExceptionsProps } from './useIssueExceptions';

type Props = Pick<IssuesContainerProps, 'params'>;

const IssueExceptionsPage: VoidFunctionComponent<Props> = ({
  params,
}) => {
  const { category } = params;
  const { type, id } = useSelector(IssuesV2Store.selectors.getScope);

  const projectFilter = type === IssueScopeKind.Project ? { projectId: id } : {};
  const releaseGroupFilter = type === IssueScopeKind.ReleaseGroup ? { releaseGroupId: id } : {};

  const tableProps = {
    category: category as UseIssueExceptionsProps['category'],
    ...projectFilter,
    ...releaseGroupFilter,
  } as UseIssueExceptionsProps;

  const {
    page,
    count,
    search,
    sortBy,
    setPage,
    setCount,
    setSearch,
    setSort,
    onRemoveExceptions,
    onExtendException,
  } = useIssueExceptions(tableProps);

  // Gate this page to only show for categories that have exceptions implemented
  if (!exceptionsEnabledCategories.includes(category)) {
    return <NotFound />;
  }

  const isOrgFreeTier = isFreeTier({
    plan: window.user?.organization?.stripe_plan,
    stripePriceLimit: window.user?.organization?.stripePriceLimit,
  });

  return (
    <div className="issue-exceptions" data-test-id="issue-exceptions">
      <BackToIssues />

      <Flex justify="space-between">
        <div className="issue-exceptions__header ">
          <Text.H1>Ignore Rules ({category})</Text.H1>
          <Text>
            These rules determine when {category} issues should be {ignoreOrResolveWordPast().toLowerCase()}{' '}
            automatically.
          </Text>
        </div>

        <Flex gap="8px">
          <ExportIgnoreRulesButton
            filters={tableProps}
            search={search}
            sortBy={sortBy}
          />
          {type !== IssueScopeKind.ReleaseGroup && (
            <PaidFeatureGate isRestricted={isOrgFreeTier}>
              <AddIgnoreRuleButton projectId={tableProps.projectId} />
            </PaidFeatureGate>
          )}
        </Flex>
      </Flex>

      <div className="issue-exceptions__content margin-top-md">
        <IssueExceptionsHeader
          page={page}
          count={count}
          setPage={setPage}
          search={search}
          sortBy={sortBy}
          onChangeSortBy={setSort}
          onChangeSearch={setSearch}
        />
        <IssueExceptionsTable
          category={tableProps.category}
          releaseGroupId={tableProps.releaseGroupId}
          projectId={tableProps.projectId}
          page={page}
          count={count}
          setPage={setPage}
          setCount={setCount}
          onRemoveExceptions={onRemoveExceptions}
          onExtendException={onExtendException}
        />
      </div>
    </div>
  );
};

export default IssueExceptionsPage;
